import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import InputField from './components/input/InputField';
import { apiDelete, apiGet } from './api';
import { ITemplate } from './models';
import './TemplateSelector.scss';

function Loading() {
    return (
        <div className="d-flex justify-content-center my-3">
            <Spinner animation="border" role="status" />
        </div>
    );
}

enum ModalState {
    HIDE,
    SHOW
}

interface IDeleteModalProps {
    state: ModalState;
    onCancel: () => void;
    onSuccess: () => void;
    template_id: string;
}

interface ITreeNode {
    __children: { [key: string]: ITreeNode };
    __template: ITemplate | null;
}

function DeleteModal({ state, onCancel, onSuccess, template_id }: IDeleteModalProps) {
    const show = state !== ModalState.HIDE;
    const [error, setError] = useState<string>('');

    const onSubmit = (e: React.FormEvent) => {
        setError('');
        e.preventDefault();
        apiDelete(`/automation/api/template/${template_id}/delete`)
            .then(onSuccess)
            .catch(e => {
                setError(`Что-то пошло не так: ${e.message}`);
            });
    };

    const cancel = () => {
        setError('');
        onCancel();
    };

    return (
        <Modal show={show} onHide={cancel} centered>
            <Modal.Header closeButton>
                <Modal.Title>Удалить шаблон?</Modal.Title>
            </Modal.Header>
            <Form onSubmit={onSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" type="submit">Да</Button>
                    <Button variant="secondary" onClick={cancel}>Нет</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

const buildCategoryTree = (templates: ITemplate[]) => {
    const root: ITreeNode = { __children: {}, __template: null };
    const uncategorized: ITemplate[] = [];

    if (!root.__children['Пользовательские']) {
        root.__children['Пользовательские'] = { __children: {}, __template: null };
    }

    templates.forEach(template => {
        const parts = template.name.split(' \\ ');
        if (parts.length === 1) {
            if (!template.system) {
                root.__children['Пользовательские'].__children[template.name] = {
                    __children: {},
                    __template: template
                };
            } else {
                uncategorized.push(template);
            }
        } else {
            let currentLevel: ITreeNode = root;
            parts.forEach((part, index) => {
                if (!currentLevel.__children[part]) {
                    currentLevel.__children[part] = {
                        __children: {},
                        __template: index === parts.length - 1 ? template : null
                    };
                }
                currentLevel = currentLevel.__children[part];
            });
        }
    });

    return { root, uncategorized };
};

const shouldNodeRender = (
    node: { [key: string]: ITreeNode },
    query: string
): boolean => {
    return Object.keys(node).some(key => {
        const currentNode = node[key];
        const isCurrentMatch =
            key.toLowerCase().includes(query.toLowerCase()) ||
            (currentNode.__template && currentNode.__template.name.toLowerCase().includes(query.toLowerCase()));

        if (isCurrentMatch) {
            return true;
        }

        return shouldNodeRender(currentNode.__children, query);
    });
};

function PolicySelector() {
    const [template_id, setDeleteModalTemplateId] = useState<string>('');
    const [modal, setModal] = useState<ModalState>(ModalState.HIDE);
    const [templates, setTemplates] = useState<ITemplate[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [activeKeys, setActiveKeys] = useState<{ [path: string]: boolean }>({});

    const loadTemplates = () => {
        setModal(ModalState.HIDE);
        apiGet<ITemplate[]>('/automation/api/templates/')
            .then(setTemplates)
            .catch(() => setTemplates([]));
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    const { root, uncategorized } = useMemo(() => buildCategoryTree(templates), [templates]);

    const toggleAccordion = (path: string) => {
        setActiveKeys(prev => ({
            ...prev,
            [path]: !prev[path]
        }));
    };

    const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(e.target.value);

        if (!query) {
            setActiveKeys({});
            return;
        }

        const newActiveKeys: { [path: string]: boolean } = {};
        const updateActiveKeysForMatches = (node: { [key: string]: ITreeNode }, path: string[]) => {
            Object.keys(node).forEach(key => {
                const currentNode = node[key];
                const currentPath = [...path, key].join(' \\ ');

                if (shouldNodeRender(currentNode.__children, query) || (currentNode.__template && currentNode.__template.name.toLowerCase().includes(query))) {
                    newActiveKeys[currentPath] = true;
                }

                updateActiveKeysForMatches(currentNode.__children, [...path, key]);
            });
        };

        updateActiveKeysForMatches(root.__children, []);
        setActiveKeys(newActiveKeys);
    };

    const handleSearchClear = () => {
        setSearchQuery('');
        setActiveKeys({});
    };

    const renderTemplate = useCallback(
        (template: ITemplate) => (
            <div key={template.id} className="rd-tpl-accordion__item">
                <Link
                    to={`/automation/policy/create/?template=${encodeURIComponent(template.id)}`}
                    className="rd-tpl-accordion__link"
                >
                    {template.name.split(' \\ ').pop()}
                </Link>
                <div className="rd-tpl-accordion__actions">
                    {!template.system && (
                        <button
                            type="button"
                            className="rd-tpl-accordion__delete-btn"
                            onClick={() => {
                                setModal(ModalState.SHOW);
                                setDeleteModalTemplateId(template.id);
                            }}
                        >
                            Удалить
                        </button>
                    )}
                    {template.system && (
                        <span className="rd-tpl-badge rd-tpl-badge--system">системный</span>
                    )}
                </div>
            </div>
        ),
        [setModal, setDeleteModalTemplateId]
    );

    const renderTree = useCallback(
        (node: { [key: string]: ITreeNode }, path: string[]) => (
            Object.keys(node).map((key) => {
                const currentNode = node[key];
                const currentPath = [...path, key].join(' \\ ');
                const isExpanded = !!activeKeys[currentPath];
                const shouldRender =
                    shouldNodeRender(currentNode.__children, searchQuery) ||
                    shouldNodeRender({ [key]: currentNode }, searchQuery);

                if (!shouldRender) return null;

                if (currentNode.__template) return null;

                const childEntries = Object.entries(currentNode.__children);
                const templateChildren = childEntries
                    .filter(([, n]) => !!n.__template)
                    .map(([, n]) => n.__template!)
                    .filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()));

                const folderChildren = Object.fromEntries(
                    childEntries.filter(([, n]) => !n.__template)
                );

                return (
                    <div
                        className={`rd-tpl-accordion ${isExpanded ? 'rd-tpl-accordion--open' : ''}`}
                        key={currentPath}
                    >
                        <div
                            className="rd-tpl-accordion__header"
                            onClick={() => toggleAccordion(currentPath)}
                        >
                            <i className="rd-icon-arrow-down rd-tpl-accordion__chevron" />
                            <span className="rd-tpl-accordion__title">{key}</span>
                        </div>

                        <div className="rd-tpl-accordion__body">
                            <div className="rd-tpl-accordion__body-inner">
                                {templateChildren.length > 0 && (
                                    <div className="rd-tpl-accordion__items">
                                        {templateChildren.map(renderTemplate)}
                                    </div>
                                )}

                                {Object.keys(folderChildren).length > 0 && (
                                    renderTree(folderChildren as any, [...path, key])
                                )}
                            </div>
                        </div>
                    </div>
                );
            })
        ),
        [searchQuery, activeKeys, renderTemplate]
    );

    const renderUncategorized = useCallback(
        () => {
            const items = uncategorized
                .filter(template => template.name.toLowerCase().includes(searchQuery.toLowerCase()));
            if (items.length === 0) return null;
            return (
                <div className="rd-tpl-selector__uncategorized">
                    {items.map(renderTemplate)}
                </div>
            );
        },
        [uncategorized, searchQuery, renderTemplate]
    );

    return templates.length > 0 ? (
        <div className="rd-tpl-selector">
            <div className="rd-tpl-selector__search">
                <InputField
                    name="search"
                    value={searchQuery}
                    label="Поиск"
                    onChange={handleSearchQueryChange}
                    onClear={handleSearchClear}
                />
            </div>
            <DeleteModal
                state={modal}
                onCancel={() => setModal(ModalState.HIDE)}
                onSuccess={loadTemplates}
                template_id={template_id}
            />
            <div className="rd-tpl-selector__list">
                {renderTree(root.__children, [])}
            </div>
            {renderUncategorized()}
        </div>
    ) : (
        <Loading />
    );
}

export default PolicySelector;
